import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  FaPlus, FaEdit, FaTrash, FaEye, FaSave, FaSearch,
  FaCheckCircle, FaUndo, FaDownload, FaFileAlt, FaRobot, FaHourglassHalf
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { vrlAPI, apiMasterAPI, aiAPI } from '../services/api';
import FloatingScrollWrapper from '../components/FloatingScrollWrapper';
import { Modal } from '../components/Modals';
import AIResultPanel from '../components/AIResultPanel';
import useAIAction from '../hooks/useAIAction';
import { ActionsMenu } from '../components/ActionsMenu';
import { StatCard } from '../components/UnifiedUIComponents';
import TablePagination from '../components/TablePagination';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const toFileUrl = (file) => {
  if (!file) return '';
  if (typeof file === 'string' && /^https?:\/\//i.test(file)) return file;
  return `${API_ORIGIN}/uploads/${file}`;
};

const RESIDUE_TYPE_OPTIONS = ['API Residue', 'Detergent Residue', 'Degradant', 'Other'];
const DISTANCE_UNIT_OPTIONS = ['cm', 'inch', 'mm', 'm'];

const EMPTY_FORM = {
  study_code: '', api_id: '', residue_type: '', surface_material: '',
  lighting_conditions: '', viewing_distance: '', distance_unit: 'cm', study_conclusion: '',
  study_document_existing: '', observer_name: '', residue_load_value: '', residue_load_unit: 'µg',
  pass_fail_result: '',
};

const VRL = () => {
  const [studies, setStudies] = useState([]);
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'form'
  const [editingId, setEditingId] = useState(null);
  // An approved study, or any study opened with View, is shown read-only.
  const [viewOnly, setViewOnly] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [studyFile, setStudyFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const ai = useAIAction();

  let currentUserRole = '';
  try {
    currentUserRole = JSON.parse(localStorage.getItem('user') || '{}')?.role || '';
  } catch (error) {
    currentUserRole = '';
  }
  const canManage = ['QA', 'Admin', 'Super Admin'].includes(currentUserRole);
  const canDeleteApproved = ['Admin', 'Super Admin'].includes(currentUserRole);

  const loadStudies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await vrlAPI.getAll();
      setStudies(response?.data?.data || []);
    } catch (error) {
      toast.error('Failed to load VRL studies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStudies(); }, [loadStudies]);

  useEffect(() => {
    const loadApis = async () => {
      try {
        const response = await apiMasterAPI.getAll({ limit: 2000, sortBy: 'api_name', sortOrder: 'asc' });
        setApis(response?.data?.data?.rows || []);
      } catch (error) {
        // Non-fatal — API dropdown just stays empty
      }
    };
    loadApis();
  }, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleNew = () => {
    setViewOnly(false);
    setForm({ ...EMPTY_FORM });
    setStudyFile(null);
    setEditingId(null);
    setView('form');
  };

  const handleEdit = (study, readOnly = false) => {
    setViewOnly(readOnly || !!study.is_approved);
    setForm({
      study_code: study.study_code || '',
      api_id: study.api_id ?? '',
      residue_type: study.residue_type || '',
      surface_material: study.surface_material || '',
      lighting_conditions: study.lighting_conditions || '',
      viewing_distance: study.viewing_distance ?? '',
      distance_unit: study.distance_unit || 'cm',
      study_conclusion: study.study_conclusion || '',
      study_document_existing: study.study_document || '',
      observer_name: study.observer_name || study.performed_by_username || '',
      residue_load_value: study.residue_load_value ?? '',
      residue_load_unit: study.residue_load_unit || 'µg',
      pass_fail_result: study.pass_fail_result || '',
    });
    setStudyFile(null);
    setEditingId(study.vrl_id);
    setView('form');
  };

  const handleDelete = async (study) => {
    if (!window.confirm(`Deactivate VRL study "${study.study_code}"? It will be removed from the active list.`)) return;
    try {
      await vrlAPI.delete(study.vrl_id);
      toast.success('VRL study deactivated');
      loadStudies();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to deactivate VRL study');
    }
  };

  const [esign, setEsign] = useState(null); // { mode: 'approve'|'unapprove', study, password, comment }
  const [esigning, setEsigning] = useState(false);
  const closeEsign = () => { setEsign(null); setEsigning(false); };
  const handleApprove = (study) => setEsign({ mode: 'approve', study, password: '', comment: '' });
  const handleUnapprove = (study) => setEsign({ mode: 'unapprove', study, password: '', comment: '' });

  const confirmEsign = async () => {
    if (!esign?.password) return toast.error('Password required');
    if (!esign.comment.trim()) return toast.error(esign.mode === 'approve' ? 'Approval rationale is required' : 'Reason for revoking approval is required');
    setEsigning(true);
    try {
      if (esign.mode === 'approve') {
        const res = await vrlAPI.approve(esign.study.vrl_id, { password: esign.password, comment: esign.comment.trim() });
        if (res?.data?.fullyApproved === false) {
          toast.info(res.data.message || `Signed. Awaiting level ${res.data.nextLevel} approval.`);
        } else {
          toast.success('VRL study approved');
        }
      } else {
        await vrlAPI.unapprove(esign.study.vrl_id, { password: esign.password, comment: esign.comment.trim() });
        toast.success('Approval revoked');
      }
      loadStudies();
      closeEsign();
    } catch (error) {
      toast.error(error?.response?.data?.message || `Failed to ${esign.mode === 'approve' ? 'approve VRL study' : 'revoke approval'}`);
      setEsigning(false);
    }
  };

  const handleSave = async () => {
    if (!form.study_code.trim()) return toast.warning('Study Code is required');
    if (studyFile && studyFile.type !== 'application/pdf') return toast.warning('Study document must be a PDF file');

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('study_code', form.study_code.trim());
      formData.append('api_id', form.api_id || '');
      formData.append('residue_type', form.residue_type || '');
      formData.append('surface_material', form.surface_material || '');
      formData.append('lighting_conditions', form.lighting_conditions || '');
      formData.append('viewing_distance', form.viewing_distance === '' ? '' : form.viewing_distance);
      formData.append('distance_unit', form.distance_unit || '');
      formData.append('study_conclusion', form.study_conclusion || '');
      formData.append('observer_name', form.observer_name || '');
      formData.append('residue_load_value', form.residue_load_value === '' ? '' : form.residue_load_value);
      formData.append('residue_load_unit', form.residue_load_unit || '');
      formData.append('pass_fail_result', form.pass_fail_result || '');
      if (studyFile) formData.append('study_document', studyFile, studyFile.name);

      let saveResponse;
      if (editingId) {
        saveResponse = await vrlAPI.update(editingId, formData);
        toast.success('VRL study updated');
      } else {
        saveResponse = await vrlAPI.create(formData);
        toast.success('VRL study created');
      }
      if (saveResponse?.data?.warning) toast.warning(saveResponse.data.warning, { autoClose: 10000 });
      await loadStudies();
      setView('list');
      setEditingId(null);
      setStudyFile(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const filteredStudies = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return studies.filter((s) => {
      const matchQ = !q
        || (s.study_code || '').toLowerCase().includes(q)
        || (s.api_name || '').toLowerCase().includes(q)
        || (s.surface_material || '').toLowerCase().includes(q);
      const matchStatus = statusFilter === 'All'
        || (statusFilter === 'Approved' && s.is_approved)
        || (statusFilter === 'Pending' && !s.is_approved);
      return matchQ && matchStatus;
    });
  }, [studies, searchQuery, statusFilter]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const totalPages = Math.max(1, Math.ceil(filteredStudies.length / itemsPerPage));
  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter, itemsPerPage]);
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [currentPage, totalPages]);
  const paginatedStudies = filteredStudies.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const studyCounts = useMemo(() => {
    const approved = studies.filter((s) => s.is_approved).length;
    return { total: studies.length, approved, pending: studies.length - approved };
  }, [studies]);
  const toggleStatusCard = (value) => setStatusFilter((cur) => (cur === value ? 'All' : value));

  const exportToExcel = () => {
    if (filteredStudies.length === 0) {
      toast.info('No VRL studies available to export');
      return;
    }
    const rows = filteredStudies.map((s) => ({
      'Study Code': s.study_code || '',
      'API': s.api_name || '',
      'Residue Type': s.residue_type || '',
      'Surface Material': s.surface_material || '',
      'Lighting Conditions': s.lighting_conditions || '',
      'Viewing Distance': s.viewing_distance ?? '',
      'Distance Unit': s.distance_unit || '',
      'Study Conclusion': s.study_conclusion || '',
      'Status': s.is_approved ? 'Approved' : 'Pending',
      'Approved By': s.approved_by_name || '',
      'Approved At': s.approved_at ? new Date(s.approved_at).toLocaleString() : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Object.keys(rows[0] || {}).map((key) => ({ wch: Math.min(Math.max(String(key).length + 6, 14), 40) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'VRL Studies');
    XLSX.writeFile(wb, `vrl_studies_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Excel exported successfully');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: 'var(--text-secondary)' }}>
        Loading...
      </div>
    );
  }

  const studyFormModal = (
    <Modal
      isOpen={view === 'form'}
      onClose={() => { setView('list'); setEditingId(null); }}
      title={<><FaEye /> {!editingId ? 'New' : (viewOnly || !canManage) ? 'View' : 'Edit'} VRL Study</>}
      size="lg"
      footer={
        <>
          <button className="btn btn-outline" onClick={() => { setView('list'); setEditingId(null); }}>{canManage && !viewOnly ? 'Cancel' : 'Close'}</button>
          {canManage && !viewOnly && (
            <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaSave /> {saving ? 'Saving...' : (editingId ? 'Update Study' : 'Save Study')}
            </button>
          )}
        </>
      }
    >
      {!canManage && (
        <div style={{ background: 'var(--warning-bg)', border: '1.5px solid var(--warning-border)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', fontSize: 'var(--fs-md)', color: 'var(--warning-text)' }}>
          Read-only view. Only QA, Admin, or Super Admin can create or edit VRL studies.
        </div>
      )}

      <fieldset disabled={!canManage || viewOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: 'var(--fs-lg)', fontWeight: 700 }}>Identification</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Study Code *</label>
              <input className="form-control form-control-sm" value={form.study_code} onChange={(e) => setField('study_code', e.target.value)} placeholder="e.g. VRL-001" />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>API</label>
              <select className="form-control form-control-sm" value={form.api_id} onChange={(e) => setField('api_id', e.target.value)}>
                <option value="">— Select —</option>
                {apis.map((a) => <option key={a.api_id} value={a.api_id}>{a.api_name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Residue Type</label>
              <select className="form-control form-control-sm" value={form.residue_type} onChange={(e) => setField('residue_type', e.target.value)}>
                <option value="">— Select —</option>
                {RESIDUE_TYPE_OPTIONS.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: 'var(--fs-lg)', fontWeight: 700 }}>Visual Inspection Conditions</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Surface Material</label>
              <input className="form-control form-control-sm" value={form.surface_material} onChange={(e) => setField('surface_material', e.target.value)} placeholder="e.g. SS316L, Glass, PTFE" />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Viewing Distance</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input className="form-control form-control-sm" type="number" step="any" min="0" value={form.viewing_distance} onChange={(e) => setField('viewing_distance', e.target.value)} style={{ flex: 2 }} />
                <select className="form-control form-control-sm" value={form.distance_unit} onChange={(e) => setField('distance_unit', e.target.value)} style={{ flex: 1 }}>
                  {DISTANCE_UNIT_OPTIONS.map((u) => <option key={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Residue Load Spiked</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input className="form-control form-control-sm" type="number" step="any" min="0" value={form.residue_load_value} onChange={(e) => setField('residue_load_value', e.target.value)} placeholder="e.g. 4" style={{ flex: 2 }} />
                <select className="form-control form-control-sm" value={form.residue_load_unit} onChange={(e) => setField('residue_load_unit', e.target.value)} style={{ flex: 1 }}>
                  {['µg', 'mg', 'µg/cm²', 'mg/cm²'].map((u) => <option key={u}>{u}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div style={{ marginTop: '14px' }}>
            <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Lighting Conditions</label>
            <textarea className="form-control form-control-sm" rows={2} value={form.lighting_conditions} onChange={(e) => setField('lighting_conditions', e.target.value)} placeholder="e.g. White light, ≥300 lux at surface" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginTop: '14px' }}>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Observer (who performed the assessment)</label>
              <input className="form-control form-control-sm" value={form.observer_name} onChange={(e) => setField('observer_name', e.target.value)} placeholder="Observer name" />
              <small className="text-muted">Checked against mandatory Visual Inspection training for the logged-in user when this study is saved.</small>
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Pass / Fail</label>
              <select className="form-control form-control-sm" value={form.pass_fail_result} onChange={(e) => setField('pass_fail_result', e.target.value)}>
                <option value="">— Select —</option>
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
              </select>
            </div>
          </div>
        </div>

        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
          <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Study Conclusion</label>
          <textarea className="form-control form-control-sm" rows={3} value={form.study_conclusion} onChange={(e) => setField('study_conclusion', e.target.value)} placeholder="Visual residue limit determined and rationale..." />
        </div>

        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: 'var(--fs-lg)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaFileAlt /> Study Report (PDF)
          </h3>
          <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Upload Study Document</label>
          <input
            className="form-control form-control-sm"
            type="file"
            accept="application/pdf"
            onChange={(e) => setStudyFile(e.target.files?.[0] || null)}
          />
          {form.study_document_existing && !studyFile && (
            <a href={toFileUrl(form.study_document_existing)} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: 'var(--fs-xs)', color: 'var(--primary-color)', marginTop: '4px' }}>
              <FaDownload /> Current file on record (upload a new PDF to replace it)
            </a>
          )}
        </div>
      </fieldset>
    </Modal>
  );

  return (
    <>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 className="page-title">VRL (Visual Residue Limit) Studies</h2>
            <p className="page-subtitle">Visual detectability studies establishing the residue limit visible on equipment surfaces under defined lighting and viewing conditions.</p>
          </div>
          <div className="toolbar-full" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-outline"
              type="button"
              disabled={filteredStudies.length === 0}
              title={filteredStudies.length ? 'AI-draft a report narrative from the current VRL studies' : 'No VRL studies to summarize yet'}
              data-module="ai_assistant" onClick={() => ai.run('AI Draft Report — VRL Studies', () => aiAPI.draftReport({
                reportType: 'vrl',
                data: filteredStudies.map((s) => ({
                  study_code: s.study_code, api: s.api_name, residue_type: s.residue_type,
                  surface_material: s.surface_material, lighting_conditions: s.lighting_conditions,
                  viewing_distance: s.viewing_distance, distance_unit: s.distance_unit,
                  study_conclusion: s.study_conclusion, status: s.is_approved ? 'Approved' : 'Pending',
                })),
              }), 'report')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FaRobot /> AI Draft Report
            </button>
            <button className="btn btn-success" onClick={exportToExcel} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaDownload /> Excel
            </button>
            {canManage && (
              <button className="btn btn-primary" onClick={handleNew} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaPlus /> New Study
              </button>
            )}
          </div>
          <div className="toolbar-compact" style={{ gap: '8px' }}>
            {canManage && (
              <button className="btn btn-primary" onClick={handleNew} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaPlus /> New Study
              </button>
            )}
            <ActionsMenu
              title="More actions"
              items={[
                {
                  label: 'AI Draft Report', icon: <FaRobot />, module: 'ai_assistant', disabled: filteredStudies.length === 0,
                  onClick: () => ai.run('AI Draft Report — VRL Studies', () => aiAPI.draftReport({
                    reportType: 'vrl',
                    data: filteredStudies.map((s) => ({
                      study_code: s.study_code, api: s.api_name, residue_type: s.residue_type,
                      surface_material: s.surface_material, lighting_conditions: s.lighting_conditions,
                      viewing_distance: s.viewing_distance, distance_unit: s.distance_unit,
                      study_conclusion: s.study_conclusion, status: s.is_approved ? 'Approved' : 'Pending',
                    })),
                  }), 'report'),
                },
                { label: 'Export Excel', icon: <FaDownload />, onClick: exportToExcel },
              ]}
            />
          </div>
        </div>

        <div className="stat-card-grid">
          <StatCard compact filterHint title="Total Studies" value={studyCounts.total} icon={FaFileAlt} color="primary" active={statusFilter === 'All'} onClick={() => setStatusFilter('All')} hint="Show all studies" />
          <StatCard compact filterHint title="Approved" value={studyCounts.approved} icon={FaCheckCircle} color="success" active={statusFilter === 'Approved'} onClick={() => toggleStatusCard('Approved')} hint="Filter: Approved" />
          <StatCard compact filterHint title="Pending Approval" value={studyCounts.pending} icon={FaHourglassHalf} color="warning" active={statusFilter === 'Pending'} onClick={() => toggleStatusCard('Pending')} hint="Filter: Pending" />
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 220px' }}>
            <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }} />
            <input className="form-control form-control-sm" style={{ paddingLeft: '30px' }} placeholder="Search study code, API or surface..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
          <select className="form-control form-control-sm" style={{ maxWidth: '160px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="All">All Statuses</option>
            <option value="Approved">Approved</option>
            <option value="Pending">Pending</option>
          </select>
        </div>

        {filteredStudies.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
            <FaEye style={{ fontSize: 'var(--fs-3xl)', marginBottom: '12px', opacity: 0.4 }} />
            <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, marginBottom: '8px' }}>No VRL studies found</div>
            {canManage && studies.length === 0 && (
              <button className="btn btn-primary" onClick={handleNew} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                <FaPlus /> Create First Study
              </button>
            )}
          </div>
        ) : (
          <FloatingScrollWrapper>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-color)', borderBottom: '2px solid var(--border-color)' }}>
                  {['Study Code', 'API', 'Residue Type', 'Surface Material', 'Viewing Distance', 'Status', 'Actions'].map((h) => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedStudies.map((study) => (
                  <tr key={study.vrl_id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--primary-color)' }}>{study.study_code}</td>
                    <td style={{ padding: '10px 12px' }}>{study.api_name || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{study.residue_type || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{study.surface_material || '—'}</td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{study.viewing_distance ?? '—'} {study.viewing_distance ? study.distance_unit : ''}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {study.is_approved ? (
                        <span style={{ background: 'var(--success-bg)', color: 'var(--success-text)', padding: '2px 8px', borderRadius: '999px', fontSize: 'var(--fs-xs)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          Approved
                        </span>
                      ) : (
                        <span style={{ background: 'var(--warning-bg)', color: 'var(--warning-text)', padding: '2px 8px', borderRadius: '999px', fontSize: 'var(--fs-xs)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          Pending
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {canManage ? (
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <button className="btn btn-sm btn-outline" onClick={() => handleEdit(study, true)} title="View"><FaEye /></button>
                          {study.is_approved ? (
                            <button className="btn btn-sm btn-outline" onClick={() => handleUnapprove(study)} title="Revoke Approval"><FaUndo /></button>
                          ) : (
                            <button className="btn btn-sm btn-success-outline" onClick={() => handleApprove(study)} title="Approve"><FaCheckCircle /></button>
                          )}
                          <ActionsMenu items={[
                            { label: 'Edit', icon: <FaEdit />, onClick: () => handleEdit(study), show: !study.is_approved },
                            { label: 'Deactivate', icon: <FaTrash />, onClick: () => handleDelete(study), show: !study.is_approved || canDeleteApproved, danger: true },
                          ]} />
                        </div>
                      ) : (
                        <button className="btn btn-sm btn-outline" onClick={() => handleEdit(study, true)} title="View"><FaEye /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </FloatingScrollWrapper>
        )}
        {filteredStudies.length > 0 && (
          <TablePagination total={filteredStudies.length} page={currentPage} pageSize={itemsPerPage} onPageChange={setCurrentPage} onPageSizeChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }} itemLabel="studies" />
        )}
      </div>
      {studyFormModal}
      <AIResultPanel
        isOpen={ai.isOpen}
        onClose={ai.close}
        title={ai.title}
        loading={ai.loading}
        error={ai.error}
        content={ai.content}
        docType="report"
        exportFileName="vrl-ai-report.pdf"
      />
      {esign && (
        <Modal
          isOpen
          onClose={closeEsign}
          title={`${esign.mode === 'approve' ? 'Approve' : 'Revoke Approval for'} VRL Study — ${esign.study.study_code} — Electronic Signature`}
          footer={
            <>
              <button className="btn btn-outline" onClick={closeEsign} disabled={esigning}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmEsign} disabled={esigning}>{esigning ? 'Processing...' : esign.mode === 'approve' ? 'Confirm Approval' : 'Confirm Revocation'}</button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">{esign.mode === 'approve' ? 'Approval Rationale' : 'Reason for Revoking Approval'} *</label>
            <textarea className="form-control" rows={2} value={esign.comment} onChange={(e) => setEsign((p) => ({ ...p, comment: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Login Password *</label>
            <input type="password" className="form-control" autoComplete="new-password" value={esign.password}
              onChange={(e) => setEsign((p) => ({ ...p, password: e.target.value }))}
              placeholder="Enter your login password"
              onKeyDown={(e) => { if (e.key === 'Enter') confirmEsign(); }} />
          </div>
        </Modal>
      )}
    </>
  );
};

export default VRL;
